/**
 * Codes one schema may mint in its lifetime, unless it overrides it.
 *
 * Product's number. It counts ASSIGNMENT ROWS, not allocated sequence numbers: a
 * losing race burns a sequence without creating a row, and re-minting an asset
 * already named returns the existing row without allocating at all, so the two
 * figures diverge and only one of them is "codes that exist".
 *
 * Note what spends it today: the builder's sample previews are the only callers of
 * the mint endpoint, every distinct set of values allocates a permanent row, and
 * nothing deletes one. A schema can therefore exhaust itself through exploration —
 * which is recoverable by raising its own `codeCap`, and is why that override
 * exists rather than this being a hard constant.
 *
 * Lives here because both halves read it: the API refuses a mint against it and the
 * builder warns as it approaches. They were separate literals, so raising one left
 * the other warning against a budget nobody enforced, with nothing comparing them.
 * A per-schema `codeCap` override already reaches both sides and cannot drift; only
 * this default could.
 */
export const DEFAULT_TRACKING_CODE_CAP = 4000;
/**
 * Ceiling on an override, so a typo cannot ask for a budget nothing can serve.
 * Well above the default; the point is to bound the number, not to second-guess it.
 */
export const MAX_TRACKING_CODE_CAP = 1_000_000;
/**
 * Records one dataset may hold.
 *
 * Mirrors the 20 000-row cap already documented for a shared list, because the
 * two are the same kind of thing from the user's side — a table they scroll and
 * edit — and picking a second, different number would only invite the question
 * of why they differ.
 *
 * Unlike the tracking-code cap this is not a lifetime budget: deleting records
 * frees the room again. It exists to bound the grid, not to ration it.
 *
 * Lives here for the same reason the cap above does: the frontend has no
 * literal of its own to drift from this one, and the API's refusal names the
 * number, so the message and the bound cannot disagree.
 */
export const MAX_RECORDS_PER_DATASET = 20_000;
/**
 * Longest value a single record cell may hold.
 *
 * Here rather than in either consumer because BOTH clamp against it: the grid's
 * editor caps typing and its paste clamps (`entities/taxonomy/lib/cellLimits`),
 * and the API refuses a longer one. Two literals drift the moment one is raised,
 * and the direction that bites is the client's going up — every long paste then
 * becomes a 400 the client had no way to predict.
 */
export const MAX_CELL_VALUE_LENGTH = 255;
/**
 * How many empty records one "add rows" request may append.
 *
 * The grid's footer control offers a small number at a time; this is the ceiling
 * on a hand-typed value, not the expected one.
 */
export const MAX_RECORDS_PER_CREATE = 500;
/**
 * How many records one "generate codes" request may mint for.
 *
 * Lower than every other bulk bound here, and deliberately so: this is the only
 * record operation that SPENDS something nothing can give back. Each record is
 * its own allocating transaction, so a request of this size is 200 round trips,
 * and the cost of a mistaken selection is permanent rather than undoable.
 *
 * Bounding it small keeps a mis-click cheap and keeps the confirmation honest —
 * a number a person can weigh against the schema's remaining budget.
 */
export const MAX_RECORDS_PER_MINT = 200;
